// AIDEV-NOTE: SPA-scoped Radix Popover wrapper using tw-spa-* theme tokens.
// Follows the same Object.assign composition pattern as SPADialog.
// The shared Popover in components/ui/ bakes in white bg and light-themed
// outline — those can't be reliably overridden via className, so this
// wraps the primitive directly with SPA dark theme styling.

import * as PopoverPrimitive from '@radix-ui/react-popover';
import React from 'react';
import cn from '@/utils/cn';
import { useSPAThemeScope } from './SPAThemeScope';

type PopoverContentRef = React.ElementRef<typeof PopoverPrimitive.Content>;
type PopoverContentProps = React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>;

const PopoverContent = React.forwardRef<PopoverContentRef, PopoverContentProps>((
  {
    align = 'end',
    children,
    className,
    side = 'bottom',
    sideOffset = 4,
    ...props
  },
  ref,
) => {
  // Portalled into document.body, so a legacy mount's pinned theme class has to travel with it.
  const themeScope = useSPAThemeScope();

  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        ref={ref}
        align={align}
        className={cn(
          'tw-z-[9999] tw-bg-spa-bg-secondary tw-border tw-border-solid tw-border-spa-border',
          'tw-rounded-lg tw-shadow-lg tw-p-1 tw-outline-none',
          themeScope,
          className,
        )}
        side={side}
        sideOffset={sideOffset}
        {...props}
      >
        {children}
      </PopoverPrimitive.Content>
    </PopoverPrimitive.Portal>
  );
});

PopoverContent.displayName = PopoverPrimitive.Content.displayName;

// AIDEV-NOTE: Compose onto a local wrapper, NEVER Object.assign(PopoverPrimitive.Root, ...) —
// ui/Popover mutates that same shared radix export, and whichever module evaluates last would
// overwrite the other's Content (the white-theme-picker bug).
const SPAPopoverRoot = (props: React.ComponentProps<typeof PopoverPrimitive.Root>) => (
  <PopoverPrimitive.Root {...props} />
);

const SPAPopover = Object.assign(SPAPopoverRoot, {
  Anchor: PopoverPrimitive.Anchor,
  Close: PopoverPrimitive.Close,
  Content: PopoverContent,
  Trigger: PopoverPrimitive.Trigger,
});

export default SPAPopover;
